import * as React from 'react';
import { BoardBackground } from '@/lib/boards';
import { compressImageToDataUrl } from '@/lib/image';

export type UseBoardBackgroundEditorParams = {
  initialBackgroundType?: 'gradient' | 'color' | 'image';
  initialColor?: string;
  initialGradFrom?: string;
  initialGradTo?: string;
  initialGradAngle?: number;
  initialImagePreview?: string | null;
  initialOverlayOpacity?: number; // 0-80
};

export function useBoardBackgroundEditor(params?: UseBoardBackgroundEditorParams) {
  const {
    initialBackgroundType = 'gradient',
    initialColor = '#e2e8f0',
    initialGradFrom = '#8b5cf6',
    initialGradTo = '#06b6d4',
    initialGradAngle = 135,
    initialImagePreview = null,
    initialOverlayOpacity = 0,
  } = params || {};

  const [backgroundType, setBackgroundType] = React.useState<'gradient' | 'color' | 'image'>(initialBackgroundType);
  const [colorVal, setColorVal] = React.useState<string>(initialColor);
  const [gradFrom, setGradFrom] = React.useState<string>(initialGradFrom);
  const [gradTo, setGradTo] = React.useState<string>(initialGradTo);
  const [gradAngle, setGradAngle] = React.useState<number>(initialGradAngle);
  const [imagePreview, setImagePreview] = React.useState<string | null>(initialImagePreview);
  const [overlayOpacity, setOverlayOpacity] = React.useState<number>(initialOverlayOpacity);

  const updateGradient = (from: string, to: string, angle: number) => {
    setGradFrom(from);
    setGradTo(to);
    setGradAngle(angle);
  };

  const handleImageUpload = async (file: File | null) => {
    if (!file) return;
    try {
      const dataUrl = await compressImageToDataUrl(file, { maxWidth: 1800, quality: 0.85, mimeType: 'image/jpeg' });
      setImagePreview(dataUrl);
    } catch {}
  };

  const handleOverlayChange = (opacity: number) => {
    setOverlayOpacity(opacity);
  };

  const getBackgroundValue = (): BoardBackground => {
    switch (backgroundType) {
      case 'color':
        return { type: 'color', value: colorVal };
      case 'image': {
        const overlay = overlayOpacity > 0 ? `rgba(0, 0, 0, ${overlayOpacity / 100})` : undefined;
        return { type: 'image', value: imagePreview || '', overlay };
      }
      case 'gradient':
      default:
        return { type: 'gradient', value: `linear-gradient(${gradAngle}deg, ${gradFrom}, ${gradTo})` };
    }
  };

  const syncFromBackground = (bg: BoardBackground) => {
    setBackgroundType(bg.type);
    if (bg.type === 'color') {
      setColorVal(bg.value || '#e2e8f0');
    } else if (bg.type === 'gradient') {
      const m = bg.value.match(/linear-gradient\((\d+)deg,\s*([^,]+),\s*([^)]+)\)/i);
      if (m) {
        setGradAngle(parseInt(m[1], 10));
        setGradFrom(m[2].trim());
        setGradTo(m[3].trim());
      }
    } else if (bg.type === 'image') {
      setImagePreview(bg.value || null);
      const overlay = bg.overlay;
      if (overlay) {
        const match = overlay.match(/rgba\(0,\s*0,\s*0,\s*([\d.]+)\)/);
        setOverlayOpacity(match ? Math.min(80, Math.max(0, Math.round(parseFloat(match[1]) * 100))) : 0);
      } else {
        setOverlayOpacity(0);
      }
    }
  };

  return {
    // state
    backgroundType,
    colorVal,
    gradFrom,
    gradTo,
    gradAngle,
    imagePreview,
    overlayOpacity,
    // setters/actions
    setBackgroundType,
    setColorVal,
    setGradFrom,
    setGradTo,
    setGradAngle,
    setImagePreview,
    setOverlayOpacity,
    updateGradient,
    handleImageUpload,
    handleOverlayChange,
    // helpers
    getBackgroundValue,
    syncFromBackground,
  };
}


